import { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
    Box, Typography, Button, Stack, Chip, CircularProgress,
    Input, IconButton, Avatar,
} from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';

import ArrowBackRoundedIcon        from '@mui/icons-material/ArrowBackRounded';
import RefreshRoundedIcon          from '@mui/icons-material/RefreshRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import ReceiptLongRoundedIcon      from '@mui/icons-material/ReceiptLongRounded';
import CreditCardRoundedIcon       from '@mui/icons-material/CreditCardRounded';
import PaymentsRoundedIcon         from '@mui/icons-material/PaymentsRounded';
import PointOfSaleRoundedIcon      from '@mui/icons-material/PointOfSaleRounded';
import GavelRoundedIcon            from '@mui/icons-material/GavelRounded';
import RemoveCircleOutlineRoundedIcon from '@mui/icons-material/RemoveCircleOutlineRounded';
import LocationOnRoundedIcon       from '@mui/icons-material/LocationOnRounded';
import TrendingUpRoundedIcon       from '@mui/icons-material/TrendingUpRounded';
import BarChartRoundedIcon         from '@mui/icons-material/BarChartRounded';
import PersonRoundedIcon           from '@mui/icons-material/PersonRounded';

import Header from '../../components/Header';
import { AppContext } from '../../context';
import { 
    APIGetOrders, APIGetFines, APIGetStockReport, 
    APIWorkstationStats, APIProcessSaleNotification, 
    APIDeleteOrder, APIGetPendingSaleNotifications, APIGetProducts, 
    APIGetDiscounts, APIGetProductCategories, parseApiError 
} from '../../api';
import MemeLoader from '../../components/MemeLoader';
import { businessTodayISO, businessToday } from '../../businessDay';
import ReplenishStockModal from '../../components/ReplenishStockModal';
import WriteOffStockModal  from '../../components/WriteOffStockModal';
import WriteOffRequestModal from '../../components/WriteOffRequestModal';
import CompactCheckCard from '../../components/CompactCheckCard';
import ProcessSaleModal from './ProcessSaleModal';
import CancelOrderModal from '../../components/CancelOrderModal';
import useOrderCancel from '../../hooks/useOrderCancel';
import ChangeAdminModal from '../../components/ChangeAdminModal';
import useOrderAdmin from '../../hooks/useOrderAdmin';


// ─── Утилиты ──────────────────────────────────────────────────────────────────
const toISO = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const fmtDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
};

const fmtMoney = (val) => {
    const n = parseFloat(val) || 0;
    return n.toLocaleString('ru-RU', { maximumFractionDigits: 0 });
};

const getInitials = (name = '') => {
    const parts = name.trim().split(' ');
    return (parts[0]?.[0] || '') + (parts[1]?.[0] || '');
};

// Дробное количество к виду "1,8"; целые — без дробной части
const fmtQty = (v) => {
    const n = Number(v);
    if (!isFinite(n)) return v;
    return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '').replace('.', ',');
};

const MONEY_META = {
    transfer: { label: 'Перевод',   color: '#818cf8', bg: 'rgba(129,140,248,0.10)', icon: <CreditCardRoundedIcon sx={{ fontSize: 18 }} /> },
    cash:     { label: 'Наличные',  color: '#22c55e', bg: 'rgba(34,197,94,0.10)',   icon: <PaymentsRoundedIcon sx={{ fontSize: 18 }} /> },
    terminal: { label: 'Терминал',  color: '#f59e0b', bg: 'rgba(245,158,11,0.10)',  icon: <PointOfSaleRoundedIcon sx={{ fontSize: 18 }} /> },
};

// ─── Анимации ─────────────────────────────────────────────────────────────────
const pageVariants = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1] } },
    exit:    { opacity: 0, y: -12, transition: { duration: 0.22 } },
};

const stagger = {
    hidden:  {},
    visible: { transition: { staggerChildren: 0.06 } },
};

const fadeUp = {
    hidden:  { opacity: 0, y: 14 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] } },
};

// ════════════════════════════════════════════════════════════════════
//  WorkstationStatsPage
// ════════════════════════════════════════════════════════════════════
export default function WorkstationStatsPage() {
    const { token, onRefreshTick, activeShift, showToast } = useContext(AppContext);
    const navigate = useNavigate();

    // Период по умолчанию: последние 3 дня (воркстанция глубже не смотрит).
    // Жёсткая граница «3 дня назад» считается один раз на монтировании.
    const today = useMemo(() => businessTodayISO(), []);
    const threeDaysAgo = useMemo(() => {
        const d = businessToday();
        d.setDate(d.getDate() - 2);   // включая сегодня = 3 дня (сегодня + 2 назад)
        return toISO(d);
    }, []);
    // Это максимальная глубина — раньше этой даты воркстанция смотреть не может.
    const minDate = threeDaysAgo;

    // Рабочая станция: по умолчанию показываем сегодня + вчера.
    const yesterdayISO = useMemo(() => {
        const d = businessToday();
        d.setDate(d.getDate() - 1);
        return toISO(d);
    }, []);
    const [dateFrom, setDateFrom] = useState(today);
    const [dateTo,   setDateTo]   = useState(today);

    const [orders,  setOrders]  = useState([]);
    const [fines,   setFines]   = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPhotographerId, setSelectedPhotographerId] = useState(null);
    const [selectedMoneyType, setSelectedMoneyType] = useState(null);   // null | 'transfer' | 'cash' | 'terminal'

    const [stock, setStock] = useState([]);          // [{ product_id, name, stock, sold_qty, is_unlimited, written_off, ... }]
    const [stockPending, setStockPending] = useState(0);   // продажи без оформления: по ним «продано»/остаток неполные
    // Оформление чека из статистики — та же модалка, что в разделе «Все чеки».
    const [pending,      setPending]      = useState([]);   // pending-продажи (поиск фантома по чеку)
    const [products,     setProducts]     = useState([]);
    const [discounts,    setDiscounts]    = useState([]);
    const [categories,   setCategories]   = useState([]);
    const [processModal, setProcessModal] = useState({ open: false, notification: null, loading: false });
    const [replenishOpen, setReplenishOpen] = useState(false);
    const [writeOffOpen,  setWriteOffOpen]  = useState(false);
    const [writeOffReqOpen, setWriteOffReqOpen] = useState(false);
    const [processingId, setProcessingId] = useState(null);

    const [adminRewards, setAdminRewards] = useState([]);
    const [rewardByPh, setRewardByPh] = useState({});   // { [photographer_id]: reward }

    const loadStock = useCallback(async () => {
        const bt = businessTodayISO();
        const params = { date_from: bt, date_to: bt };
        if (selectedPhotographerId != null) params.photographer_id = selectedPhotographerId;
        if (selectedMoneyType != null) params.money_type = selectedMoneyType;   // правка #2
        const data = await APIGetStockReport(token, params);
        setStock(Array.isArray(data?.items) ? data.items : []);
        setStockPending(data?.pending_sales_count || 0);
    }, [token, selectedPhotographerId, selectedMoneyType]);

    // ── Загрузка ───────────────────────────────────────────────────
    const loadAll = useCallback(async () => {
        setLoading(true);
        const params = { date_from: dateFrom, date_to: dateTo };
        const [ordersData, finesData, wsStats, pendingData, productsData, discountsData, categoriesData] = await Promise.all([
            APIGetOrders(token, params),
            APIGetFines(token, { ...params, is_active: true }),
            APIWorkstationStats(token, params),
            APIGetPendingSaleNotifications(token, { status: 'pending', date: businessTodayISO() }),
            APIGetProducts(token, { is_active: true }),
            APIGetDiscounts(token, { is_active: true }),
            APIGetProductCategories(token, { is_active: true, with_counts: true }),
        ]);
        if (Array.isArray(ordersData)) setOrders(ordersData);
        if (Array.isArray(finesData))  setFines(finesData);
        if (Array.isArray(pendingData))    setPending(pendingData);
        if (Array.isArray(productsData))   setProducts(productsData);
        if (Array.isArray(discountsData))  setDiscounts(discountsData);
        if (Array.isArray(categoriesData)) setCategories(categoriesData);
        const map = {};
        for (const r of (wsStats?.photographer_rewards || [])) {
            map[r.photographer_id] = parseFloat(r.reward || 0);
        }
        setRewardByPh(map);
        setAdminRewards(wsStats?.administrator_rewards || []);
        setLoading(false);
        return true;
    }, [token, dateFrom, dateTo]);

    // Логический чек = уникальный receipt_uid; строки без него — каждая как отдельный чек.
    const receiptKey = (o) => o.receipt_uid || `single-${o.id}`;
    const countChecks = (arr) => {
        const s = new Set();
        for (const o of arr) s.add(receiptKey(o));
        return s.size;
    };

    useEffect(() => { loadAll(); }, [loadAll]);
    useEffect(() => onRefreshTick(loadAll), [onRefreshTick, loadAll]);

    useEffect(() => { loadStock(); }, [loadStock]);
    useEffect(() => onRefreshTick(loadStock), [onRefreshTick, loadStock]);

    // Срез по выбранному фотографу (клик в «Топ фотографов»). null → вся точка.
    const viewOrders = useMemo(
        () => selectedPhotographerId == null
            ? orders
            : orders.filter(o => o.photographer === selectedPhotographerId),
        [orders, selectedPhotographerId],
    );
    const viewFines = useMemo(
        () => selectedPhotographerId == null
            ? fines
            : fines.filter(f => f.photographer === selectedPhotographerId),
        [fines, selectedPhotographerId],
    );
    // Полная выручка точки — для процентов в списках «Топ фотографов» / «По админам».
    const fullRev = useMemo(
        () => orders.filter(o => !o.is_cancelled).reduce((s, o) => s + parseFloat(o.value || 0), 0),
        [orders],
    );

    // Карточки-переключатель всегда показывают все типы; фильтруются только
    // данные ниже — чеки и «Топ фотографов» (склад фильтруется до-фетчем на сервере).
    const mtOrders = useMemo(
        () => selectedMoneyType == null ? orders : orders.filter(o => o.money_type === selectedMoneyType),
        [orders, selectedMoneyType],
    );
    const mtViewOrders = useMemo(
        () => selectedMoneyType == null ? viewOrders : viewOrders.filter(o => o.money_type === selectedMoneyType),
        [viewOrders, selectedMoneyType],
    );

    const handleConfirmOrder = async (o) => {
        if (!o.sale_notification_id) return;
        setProcessingId(o.id);
        const res = await APIProcessSaleNotification(token, o.sale_notification_id, {});
        if (res?.error) showToast(parseApiError(res.error, 'Не удалось подтвердить'), 'danger');
        else { showToast('Продажа подтверждена'); loadAll(); loadStock(); }
        setProcessingId(null);
    };

    // Подтверждение из строки: открываем то же окно оформления, что и в «Все чеки»
    // (выбор администратора + сопоставление товаров). Прямое оформление —
    // только если фантом-продажи уже нет (страховка).
    const handleRowConfirm = (order) => {
        const notif = pending.find(n => n.id === order.sale_notification_id);
        if (notif) setProcessModal({ open: true, notification: notif, loading: false });
        else handleConfirmOrder(order);
    };

    // Отправка из окна оформления: администратор и позиции берутся из payload модалки.
    const handleSubmitProcess = async (payload) => {
        const notification = processModal.notification;
        if (!notification) return;
        setProcessModal(p => ({ ...p, loading: true }));
        const res = await APIProcessSaleNotification(token, notification.id, payload);
        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось оформить чек'), 'danger');
            setProcessModal(p => ({ ...p, loading: false }));
            return;
        }
        const total = (res.orders || []).reduce((s, o) => s + parseFloat(o.value || 0), 0);
        showToast(`Чек оформлен · ${total.toLocaleString('ru-RU')} ₽`);
        setProcessModal({ open: false, notification: null, loading: false });
        loadAll();
        loadStock();
    };

    // Отмена чека — через заявку (сервер вернёт cancel_mode='request').
    const { requestCancel, cancelBusyId, cancelModal } = useOrderCancel({
        onDone: () => { loadAll(); loadStock(); },
    });
    const { changeAdmin, adminModal } = useOrderAdmin({ onDone: loadAll });

    // ── Подсчёты ───────────────────────────────────────────────────
    const stats = useMemo(() => {
        const sumOf = (arr) => arr.reduce((s, x) => s + parseFloat(x.value || 0), 0);

        const activeVO = viewOrders.filter(o => !o.is_cancelled);   // отменённые в выручку не идут

        const transfer = activeVO.filter(o => o.money_type === 'transfer');
        const cash     = activeVO.filter(o => o.money_type === 'cash');
        const terminal = activeVO.filter(o => o.money_type === 'terminal');

        const fineList      = viewFines.filter(f => !f.is_deduction);
        const deductionList = viewFines.filter(f => f.is_deduction);

        const uniqueDays = new Set(activeVO.map(o => o.date));

        const totalRev = sumOf(activeVO);
        const viewChecks = countChecks(activeVO);
        const avgCheck = viewChecks > 0 ? totalRev / viewChecks : 0;

        return {
            totalRev,
            ordersCount: countChecks(orders.filter(o => !o.is_cancelled)),
            avgCheck,
            workDays: uniqueDays.size,
            transferSum: sumOf(transfer),
            cashSum:     sumOf(cash),
            terminalSum: sumOf(terminal),
            transferCount: transfer.length,
            cashCount:     cash.length,
            terminalCount: terminal.length,
            fineSum:      sumOf(fineList),
            fineCount:    fineList.length,
            deductionSum: sumOf(deductionList),
            deductionCount: deductionList.length,
        };
    }, [viewOrders, viewFines]);

    // ── Разбивка по фотографам ─────────────────────────────────────
    const byPhotographer = useMemo(() => {
        const map = new Map();
        for (const o of orders) {
            if (o.is_cancelled) continue;
            const id = o.photographer;
            if (!map.has(id)) {
                map.set(id, {
                    id, name: o.photographer_name || '—',
                    avatar_url: o.photographer_avatar_url || null,
                    total: 0, uids: new Set(),
                    transfer: 0, cash: 0, terminal: 0,
                });
            }
            const ph = map.get(id);
            const v = parseFloat(o.value || 0);
            ph.total += v;
            ph.uids.add(o.receipt_uid || `single-${o.id}`);
            if (o.money_type === 'transfer') ph.transfer += v;
            else if (o.money_type === 'cash') ph.cash += v;
            else if (o.money_type === 'terminal') ph.terminal += v;
        }
        return Array.from(map.values())
            .map(ph => ({ ...ph, count: ph.uids.size }))
            .sort((a, b) => b.total - a.total);
    }, [mtOrders]);

    // ── Разбивка по дням ───────────────────────────────────────────
    const byDay = useMemo(() => {
        const map = new Map();
        for (const o of viewOrders) {
            if (o.is_cancelled) continue;
            const key = o.date;
            if (!map.has(key)) map.set(key, { date: key, total: 0, uids: new Set() });
            const day = map.get(key);
            day.total += parseFloat(o.value || 0);
            day.uids.add(o.receipt_uid || `single-${o.id}`);
        }
        return Array.from(map.values())
            .map(d => ({ ...d, count: d.uids.size }))
            .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    }, [viewOrders]);

    const maxDayTotal = useMemo(() => Math.max(...byDay.map(d => d.total), 1), [byDay]);

    // ── Разбивка по админам смен ───────────────────────────────────
    const byAdmin = useMemo(() => {
        const map = new Map();
        for (const o of orders) {
            if (o.is_cancelled) continue;
            if (!o.administrator || !o.administrator_name) continue;
            const id = o.administrator;
            if (!map.has(id)) {
                map.set(id, {
                    id, name: o.administrator_name,
                    avatar_url: o.administrator_avatar_url || null,
                    total: 0, uids: new Set(),
                });
            }
            const a = map.get(id);
            a.total += parseFloat(o.value || 0);
            a.uids.add(o.receipt_uid || `single-${o.id}`);
        }
        for (const row of adminRewards) {
            if (!map.has(row.administrator_id)) map.set(row.administrator_id, {
                id: row.administrator_id, name: row.administrator_name, total: 0, uids: new Set(),
            });
            map.get(row.administrator_id).reward = Number(row.reward || 0);
        }
        return Array.from(map.values())
            .map(a => ({ ...a, count: a.uids.size }))
            .sort((a, b) => b.total - a.total);
    }, [orders, adminRewards]);

    // Чеки смены, сгруппированные в один логический чек (для блока внизу страницы).
    const groupedChecks = useMemo(() => {
        const map = new Map();
        for (const o of mtViewOrders) {
            const key = o.receipt_uid || `single-${o.id}`;
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(o);
        }
        return [...map.values()].sort(
            (a, b) => new Date(b[0].date_create || 0) - new Date(a[0].date_create || 0),
        );
    }, [mtViewOrders]);

    // ── Быстрые периоды (воркстанции доступны ТОЛЬКО последние 3 дня) ──
    const quickPeriods = useMemo(() => {
        const todayDate = businessToday();
        const yesterday = new Date(todayDate); yesterday.setDate(todayDate.getDate() - 1);
        const twoDaysAgo = new Date(todayDate); twoDaysAgo.setDate(todayDate.getDate() - 2);
        return [
            { label: 'Сегодня',       from: toISO(todayDate), to: toISO(todayDate) },
            { label: 'Вчера',         from: toISO(yesterday), to: toISO(yesterday) },
            { label: 'Сегодня+Вчера', from: toISO(yesterday), to: toISO(todayDate) },
        ];
    }, []);

    return (
        <>
            <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit"
                style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}
            >
                <Header />

                <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>

                    {/* ── Шапка ────────────────────────────────────────── */}
                    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.04 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <IconButton variant="plain" onClick={() => navigate('/workstation')}
                                    sx={{ color: 'var(--joy-palette-text-secondary)' }}>
                                    <ArrowBackRoundedIcon />
                                </IconButton>
                                <Box>
                                    <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--joy-palette-text-primary)' }}>
                                        Статистика точки
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mt: 0.3 }}>
                                        <LocationOnRoundedIcon sx={{ fontSize: 14, color: 'var(--joy-palette-text-tertiary)' }} />
                                        <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)' }}>
                                            {activeShift?.location?.name || activeShift?.location_name || '—'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                            <IconButton
                                variant="outlined" color="primary"
                                onClick={loadAll}
                                sx={{
                                    // bgcolor: 'var(--joy-palette-background-level2)',
                                    // border: '1px solid var(--joy-palette-divider)',
                                }}
                            >
                                <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Box>
                    </motion.div>

                    {/* ── Фильтр периода ─────────────────────────────── */}
                    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.05 }}>
                        <Box sx={{
                            bgcolor: 'var(--joy-palette-background-level1)',
                            border: '1px solid var(--joy-palette-divider)',
                            borderRadius: 'var(--joy-radius-xl)',
                            p: { xs: 1.5, sm: 2 }, mb: 2.5,
                        }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
                                Период
                            </Typography>

                            {/* Быстрые отрезки (в пределах доступных 3 дней) */}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mb: 1.2 }}>
                                {[
                                    { key: 'today',     label: 'Сегодня', from: today,        to: today },
                                    { key: 'yesterday', label: 'Вчера',   from: yesterdayISO,  to: yesterdayISO },
                                ].map(p => {
                                    const active = dateFrom === p.from && dateTo === p.to;
                                    return (
                                        <Chip key={p.key} size="sm"
                                            variant={active ? 'solid' : 'soft'}
                                            color={active ? 'primary' : 'neutral'}
                                            onClick={() => { setDateFrom(p.from); setDateTo(p.to); }}
                                            sx={{ cursor: 'pointer', fontWeight: 700 }}>
                                            {p.label}
                                        </Chip>
                                    );
                                })}
                            </Box>

                            {/* Произвольный диапазон «с — по» (ограничен окном рабочей станции) */}
                            {/* <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                <Input type="date" size="sm" value={dateFrom}
                                    slotProps={{ input: { min: minDate, max: dateTo } }}
                                    onChange={(e) => setDateFrom(e.target.value)}
                                    sx={{ flex: 1, minWidth: 150 }} />
                                <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-tertiary)' }}>—</Typography>
                                <Input type="date" size="sm" value={dateTo}
                                    slotProps={{ input: { min: dateFrom, max: today } }}
                                    onChange={(e) => setDateTo(e.target.value)}
                                    sx={{ flex: 1, minWidth: 150 }} />
                            </Box> */}
                        </Box>
                    </motion.div>

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                            <MemeLoader />
                        </Box>
                    ) : (
                        <motion.div variants={stagger} initial="hidden" animate="visible">
                            <Stack spacing={2.5}>

                                {/* ── Главные метрики ─────────────────── */}
                                <motion.div variants={fadeUp}>
                                    <Box sx={{
                                        bgcolor: 'var(--joy-palette-background-level1)',
                                        border: '1px solid var(--joy-palette-primary-500)',
                                        borderRadius: 'var(--joy-radius-xl)',
                                        p: { xs: 2, sm: 2.5 },
                                        background: 'linear-gradient(135deg, rgba(162,38,226,0.10) 0%, rgba(162,38,226,0.02) 100%)',
                                    }}>
                                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
                                            Выручка за период
                                        </Typography>
                                        <Typography sx={{ fontSize: 36, fontWeight: 900, color: 'var(--joy-palette-primary-300)', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                                            {fmtMoney(stats.totalRev)} ₽
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 2, mt: 1.2, flexWrap: 'wrap' }}>
                                            <Box>
                                                <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                                    Чеков
                                                </Typography>
                                                <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                    {stats.ordersCount}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                                    Средний чек
                                                </Typography>
                                                <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                    {fmtMoney(stats.avgCheck)} ₽
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                                    Рабочих дней
                                                </Typography>
                                                <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                    {stats.workDays}
                                                </Typography>
                                            </Box>
                                            {stats.workDays > 0 && (
                                                <Box>
                                                    <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                                        В день
                                                    </Typography>
                                                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#22c55e' }}>
                                                        {fmtMoney(stats.totalRev / stats.workDays)} ₽
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                </motion.div>

                                {/* ── Склад / остатки ─────────────────────────── */}
                                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.06 }}>
                                    <Box sx={{
                                        bgcolor: 'var(--joy-palette-background-level1)',
                                        border: '1px solid var(--joy-palette-divider)',
                                        borderRadius: 'var(--joy-radius-xl)',
                                        p: { xs: 1.5, sm: 2 }, mb: 2.5,
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                                            <Typography sx={{ fontSize: 15, fontWeight: 700 }}>Склад · продано за смену</Typography>
                                            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                                                <Button size="sm" variant="outlined" color="danger" onClick={() => setWriteOffOpen(true)}
                                                    sx={{ fontWeight: 700 }}>
                                                    Списать бумагу
                                                </Button>
                                                <Button size="sm" variant="outlined" color="warning" onClick={() => setWriteOffReqOpen(true)}
                                                    sx={{ fontWeight: 700 }}>
                                                    Списать не бумагу
                                                </Button>
                                                <Button size="sm" variant="outlined" onClick={() => setReplenishOpen(true)}
                                                    sx={{ fontWeight: 700, borderColor: 'var(--joy-palette-primary-500)', color: 'var(--joy-palette-primary-300)' }}>
                                                    Оформить привоз
                                                </Button>
                                            </Stack>
                                        </Box>

                                        {stockPending > 0 && (
                                            <Box sx={{
                                                mb: 1.5, px: 1.2, py: 1,
                                                borderRadius: 'var(--joy-radius-sm)',
                                                bgcolor: 'rgba(245,158,11,0.12)',
                                                border: '1px solid rgba(245,158,11,0.35)',
                                            }}>
                                                <Typography sx={{ fontSize: 12, color: '#f59e0b', fontWeight: 700 }}>
                                                    ⏳ {stockPending} продаж(и) ещё не оформлены — по ним товар и остаток пока не учтены.
                                                    Сначала оформи их в «Ожидают оформления», иначе «продано» будет неполным.
                                                </Typography>
                                            </Box>
                                        )}

                                        <Stack spacing={0.6}>
                                            {stock.length === 0 ? (
                                                <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', py: 1 }}>
                                                    Нет позиций
                                                </Typography>
                                            ) : stock.map(it => (
                                                <Box key={it.product_id} sx={{
                                                    display: 'flex', alignItems: 'center', gap: 1,
                                                    px: 1.2, py: 0.8,
                                                    bgcolor: 'var(--joy-palette-background-level2)',
                                                    borderRadius: 'var(--joy-radius-sm)',
                                                }}>
                                                    <Typography sx={{ fontSize: 13, fontWeight: 600, flex: 1, minWidth: 0 }}>
                                                        {it.name}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)' }}>
                                                        продано: <b style={{ color: 'var(--joy-palette-text-primary)' }}>{it.sold_qty}</b>
                                                        {Number(it.written_off) > 0 && (
                                                            <> · списано: <b style={{ color: '#f59e0b' }}>{fmtQty(it.written_off)}</b></>
                                                        )}
                                                    </Typography>
                                                    <Chip size="sm" variant="soft" sx={{
                                                        fontSize: 10, fontWeight: 700,
                                                        bgcolor: (it.is_unlimited || Number(it.stock) > 0) ? 'rgba(34,197,94,0.12)' : 'rgba(248,113,113,0.14)',
                                                        color:   (it.is_unlimited || Number(it.stock) > 0) ? '#22c55e' : '#f87171',
                                                    }}>
                                                        остаток: {it.is_unlimited ? '∞' : fmtQty(it.stock)}
                                                    </Chip>
                                                </Box>
                                            ))}
                                        </Stack>
                                    </Box>
                                </motion.div>

                                {/* ── По типам оплаты ─────────────────── */}
                                <motion.div variants={fadeUp}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', flex: 1 }}>
                                            По типам оплаты
                                        </Typography>
                                        {selectedMoneyType != null && (
                                            <Chip size="sm" variant="soft" color="primary"
                                                onClick={() => setSelectedMoneyType(null)}
                                                endDecorator={<span style={{ fontWeight: 800 }}>✕</span>}
                                                sx={{ cursor: 'pointer', fontWeight: 700 }}>
                                                Сбросить фильтр
                                            </Chip>
                                        )}
                                    </Box>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
                                        {[
                                            { key: 'transfer', sum: stats.transferSum, count: stats.transferCount },
                                            { key: 'cash',     sum: stats.cashSum,     count: stats.cashCount },
                                            { key: 'terminal', sum: stats.terminalSum, count: stats.terminalCount },
                                        ].map(t => {
                                            const meta = MONEY_META[t.key];
                                            const pct = stats.totalRev > 0 ? (t.sum / stats.totalRev * 100) : 0;
                                            return (
                                                <Box key={t.key}
                                                    onClick={() => setSelectedMoneyType(prev => prev === t.key ? null : t.key)}
                                                    sx={{
                                                    px: 2, py: 1.8,
                                                    borderRadius: 'var(--joy-radius-lg)',
                                                    border: selectedMoneyType === t.key
                                                        ? `1.5px solid ${meta.color}`
                                                        : '1px solid var(--joy-palette-divider)',
                                                    bgcolor: meta.bg,
                                                    cursor: 'pointer',
                                                    opacity: selectedMoneyType == null || selectedMoneyType === t.key ? 1 : 0.5,
                                                    transition: 'opacity .15s, border-color .15s',
                                                    '&:hover': { borderColor: meta.color },
                                                }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                                        <Box sx={{ color: meta.color }}>{meta.icon}</Box>
                                                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: meta.color }}>
                                                            {pct.toFixed(0)}%
                                                        </Typography>
                                                    </Box>
                                                    <Typography sx={{ fontSize: 20, fontWeight: 800, color: meta.color, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                                                        {fmtMoney(t.sum)} ₽
                                                    </Typography>
                                                    <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-secondary)', mt: 0.4 }}>
                                                        {meta.label}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', mt: 0.2 }}>
                                                        {t.count} чек.
                                                    </Typography>
                                                    {/* мини-прогрессбар */}
                                                    <Box sx={{
                                                        mt: 1, height: 4, borderRadius: 2,
                                                        bgcolor: 'var(--joy-palette-background-level2)', overflow: 'hidden',
                                                    }}>
                                                        <Box sx={{
                                                            height: '100%', width: `${pct}%`,
                                                            bgcolor: meta.color,
                                                            transition: 'width .5s ease',
                                                        }} />
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </motion.div>

                                {/* ── Штрафы и списания ────────────────── */}
                                {(stats.fineCount > 0 || stats.deductionCount > 0) && (
                                    <motion.div variants={fadeUp}>
                                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
                                            Штрафы и списания
                                        </Typography>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5 }}>
                                            <Box sx={{
                                                px: 2, py: 1.8,
                                                borderRadius: 'var(--joy-radius-lg)',
                                                border: '1px solid var(--joy-palette-divider)',
                                                bgcolor: 'rgba(239,68,68,0.08)',
                                            }}>
                                                <Box sx={{ color: '#ef4444', mb: 0.5 }}>
                                                    <GavelRoundedIcon sx={{ fontSize: 18 }} />
                                                </Box>
                                                <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#ef4444', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                                                    −{fmtMoney(stats.fineSum)}
                                                </Typography>
                                                <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-secondary)', mt: 0.4 }}>
                                                    Штрафы
                                                </Typography>
                                                <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', mt: 0.2 }}>
                                                    {stats.fineCount} шт.
                                                </Typography>
                                            </Box>
                                            <Box sx={{
                                                px: 2, py: 1.8,
                                                borderRadius: 'var(--joy-radius-lg)',
                                                border: '1px solid var(--joy-palette-divider)',
                                                bgcolor: 'rgba(245,158,11,0.08)',
                                            }}>
                                                <Box sx={{ color: '#f59e0b', mb: 0.5 }}>
                                                    <RemoveCircleOutlineRoundedIcon sx={{ fontSize: 18 }} />
                                                </Box>
                                                <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#f59e0b', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                                                    −{fmtMoney(stats.deductionSum)}
                                                </Typography>
                                                <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-secondary)', mt: 0.4 }}>
                                                    Списания
                                                </Typography>
                                                <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', mt: 0.2 }}>
                                                    {stats.deductionCount} шт.
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </motion.div>
                                )}

                                {/* ── Топ фотографов ───────────────────── */}
                                {byPhotographer.length > 0 && (
                                    <motion.div variants={fadeUp}>
                                        <Box sx={{
                                            bgcolor: 'var(--joy-palette-background-level1)',
                                            border: '1px solid var(--joy-palette-divider)',
                                            borderRadius: 'var(--joy-radius-xl)',
                                            p: { xs: 2, sm: 2.5 },
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                                <TrendingUpRoundedIcon sx={{ fontSize: 18, color: 'var(--joy-palette-primary-400)' }} />
                                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', flex: 1 }}>
                                                    Топ фотографов · {byPhotographer.length}
                                                </Typography>
                                                {selectedPhotographerId != null && (
                                                    <Chip size="sm" variant="soft" color="primary"
                                                        onClick={() => setSelectedPhotographerId(null)}
                                                        endDecorator={<span style={{ fontWeight: 800 }}>✕</span>}
                                                        sx={{ cursor: 'pointer', fontWeight: 700 }}>
                                                        Показать всю точку
                                                    </Chip>
                                                )}
                                            </Box>
                                            <Stack spacing={0.8}>
                                                {byPhotographer.map((ph, idx) => {
                                                    const pct = fullRev > 0 ? (ph.total / fullRev * 100) : 0;
                                                    return (
                                                        <Box key={ph.id}
                                                            onClick={() => setSelectedPhotographerId(prev => prev === ph.id ? null : ph.id)}
                                                            sx={{
                                                            display: 'flex', alignItems: 'center', gap: 1.2,
                                                            px: 1.5, py: 1,
                                                            borderRadius: 'var(--joy-radius-md)',
                                                            cursor: 'pointer',
                                                            bgcolor: selectedPhotographerId === ph.id
                                                                ? 'rgba(162,38,226,0.12)'
                                                                : (idx === 0 ? 'rgba(34,197,94,0.06)' : 'transparent'),
                                                            border: selectedPhotographerId === ph.id
                                                                ? '1px solid var(--joy-palette-primary-500)'
                                                                : '1px solid transparent',
                                                            transition: 'background .15s',
                                                            '&:hover': { bgcolor: 'var(--joy-palette-background-level2)' },
                                                        }}>
                                                            <Typography sx={{
                                                                fontSize: 13, fontWeight: 800,
                                                                color: idx === 0 ? '#22c55e' : 'var(--joy-palette-text-tertiary)',
                                                                width: 22, textAlign: 'center',
                                                            }}>
                                                                {idx + 1}
                                                            </Typography>
                                                            <Avatar src={ph.avatar_url} size="sm" sx={{
                                                                width: 30, height: 30, fontSize: 11, fontWeight: 700,
                                                                bgcolor: 'rgba(162,38,226,0.15)',
                                                                color: 'var(--joy-palette-primary-300)',
                                                                border: '1.5px solid var(--joy-palette-primary-500)',
                                                                flexShrink: 0,
                                                            }}>
                                                                {getInitials(ph.name)}
                                                            </Avatar>
                                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--joy-palette-text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                    {ph.name}
                                                                </Typography>
                                                                <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                                                                    {ph.count} чек. · {pct.toFixed(1)}%
                                                                </Typography>
                                                                {rewardByPh[ph.id] > 0 && (
                                                                    <Typography component="span" sx={{ fontSize: 11, color: '#22c55e', fontWeight: 700, ml: 0.5 }}>
                                                                        · Премия: {fmtMoney(rewardByPh[ph.id])} ₽
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                            <Typography sx={{
                                                                fontSize: 14, fontWeight: 800,
                                                                color: idx === 0 ? '#22c55e' : 'var(--joy-palette-text-primary)',
                                                                fontVariantNumeric: 'tabular-nums',
                                                                flexShrink: 0,
                                                            }}>
                                                                {fmtMoney(ph.total)} ₽
                                                            </Typography>
                                                        </Box>
                                                    );
                                                })}
                                            </Stack>
                                        </Box>
                                    </motion.div>
                                )}

                                {/* ── По админам смен ────────────────────── */}
                                {byAdmin.length > 0 && selectedPhotographerId == null && (
                                    <motion.div variants={fadeUp}>
                                        <Box sx={{
                                            bgcolor: 'var(--joy-palette-background-level1)',
                                            border: '1px solid var(--joy-palette-divider)',
                                            borderRadius: 'var(--joy-radius-xl)',
                                            p: { xs: 2, sm: 2.5 },
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                                <PersonRoundedIcon sx={{ fontSize: 18, color: '#3b82f6' }} />
                                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                                    По админам смен · {byAdmin.length}
                                                </Typography>
                                            </Box>
                                            <Stack spacing={0.8}>
                                                {byAdmin.map((a) => {
                                                    const pct = fullRev > 0 ? (a.total / fullRev * 100) : 0;
                                                    return (
                                                        <Box key={a.id} sx={{
                                                            display: 'flex', alignItems: 'center', gap: 1.2,
                                                            px: 1.5, py: 1,
                                                            borderRadius: 'var(--joy-radius-md)',
                                                            transition: 'background .15s',
                                                            '&:hover': { bgcolor: 'var(--joy-palette-background-level2)' },
                                                        }}>
                                                            <Avatar src={a.avatar_url} size="sm" sx={{
                                                                width: 30, height: 30, fontSize: 11, fontWeight: 700,
                                                                bgcolor: 'rgba(59,130,246,0.15)',
                                                                color: '#3b82f6',
                                                                border: '1.5px solid #3b82f6',
                                                                flexShrink: 0,
                                                            }}>
                                                                {getInitials(a.name)}
                                                            </Avatar>
                                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--joy-palette-text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                    {a.name}
                                                                </Typography>
                                                                <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                                                                    {a.count} чек. · {pct.toFixed(1)}%
                                                                </Typography>
                                                                <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-success-400)', fontWeight: 700, overflowWrap: 'anywhere' }}>
                                                                    Премия {dateFrom === dateTo ? 'за день' : 'за период'}: {fmtMoney(a.reward || 0)} ₽
                                                                </Typography>
                                                            </Box>
                                                            <Typography sx={{
                                                                fontSize: 14, fontWeight: 800,
                                                                color: 'var(--joy-palette-text-primary)',
                                                                fontVariantNumeric: 'tabular-nums',
                                                                flexShrink: 0,
                                                            }}>
                                                                {fmtMoney(a.total)} ₽
                                                            </Typography>
                                                        </Box>
                                                    );
                                                })}
                                            </Stack>
                                        </Box>
                                    </motion.div>
                                )}

                                {/* ── По дням (мини-график) ────────────── */}
                                {byDay.length > 1 && (
                                    <motion.div variants={fadeUp}>
                                        <Box sx={{
                                            bgcolor: 'var(--joy-palette-background-level1)',
                                            border: '1px solid var(--joy-palette-divider)',
                                            borderRadius: 'var(--joy-radius-xl)',
                                            p: { xs: 2, sm: 2.5 },
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                                <BarChartRoundedIcon sx={{ fontSize: 18, color: '#06b6d4' }} />
                                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                                    Выручка по дням
                                                </Typography>
                                            </Box>
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'flex-end',
                                                gap: 0.4,
                                                height: 120,
                                                mb: 1,
                                                overflowX: 'auto',
                                                pb: 1,
                                            }}>
                                                {byDay.map(d => {
                                                    const h = Math.max(4, (d.total / maxDayTotal) * 100);
                                                    return (
                                                        <Box
                                                            key={d.date}
                                                            title={`${fmtDate(d.date)} · ${fmtMoney(d.total)} ₽ · ${d.count} чек.`}
                                                            sx={{
                                                                minWidth: 18,
                                                                flex: '1 0 18px',
                                                                height: `${h}%`,
                                                                background: 'linear-gradient(180deg, var(--joy-palette-primary-400) 0%, var(--joy-palette-primary-600) 100%)',
                                                                borderRadius: '4px 4px 0 0',
                                                                cursor: 'pointer',
                                                                transition: 'all .15s',
                                                                '&:hover': { transform: 'scaleY(1.05)', filter: 'brightness(1.2)' },
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                                                    {fmtDate(byDay[0]?.date)}
                                                </Typography>
                                                <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                                                    {fmtDate(byDay[byDay.length - 1]?.date)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </motion.div>
                                )}

                                {/* ── Чеки за смену ─────────────────────── */}
                                {groupedChecks.length > 0 && (
                                    <Box sx={{
                                        mt: 2,
                                        bgcolor: 'var(--joy-palette-background-level1)',
                                        border: '1px solid var(--joy-palette-divider)',
                                        borderRadius: 'var(--joy-radius-xl)', overflow: 'hidden',
                                    }}>
                                        <Box sx={{ px: 2, py: 1.6, borderBottom: '1px solid var(--joy-palette-divider)' }}>
                                            <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
                                                Чеки за смену · {groupedChecks.length}
                                            </Typography>
                                        </Box>
                                        <Stack spacing={1} sx={{ p: 1 }}>
                                            {groupedChecks.map((group) => (
                                                <CompactCheckCard
                                                    key={group[0].receipt_uid || group[0].id}
                                                    group={group}
                                                    onConfirm={handleRowConfirm}
                                                    onCancel={requestCancel}
                                                    onChangeAdmin={changeAdmin}
                                                    busy={group.some(o => processingId === o.id || cancelBusyId === o.id)}
                                                />
                                            ))}
                                        </Stack>
                                    </Box>
                                )}

                                {/* ── Пустое состояние ───────────────────── */}
                                {orders.length === 0 && (
                                    <motion.div variants={fadeUp}>
                                        <Box sx={{ textAlign: 'center', py: 8 }}>
                                            <ReceiptLongRoundedIcon sx={{ fontSize: 48, color: 'var(--joy-palette-text-tertiary)', mb: 1.5 }} />
                                            <Typography sx={{ fontSize: 14, color: 'var(--joy-palette-text-tertiary)' }}>
                                                Нет данных за выбранный период
                                            </Typography>
                                        </Box>
                                    </motion.div>
                                )}

                            </Stack>
                        </motion.div>
                    )}
                </Box>

                <CancelOrderModal {...cancelModal} />
                <ChangeAdminModal {...adminModal} />
            </motion.div>
            
            <ReplenishStockModal
                open={replenishOpen}
                onClose={() => setReplenishOpen(false)}
                onDone={loadStock}
            />

            <WriteOffStockModal
                open={writeOffOpen}
                onClose={() => setWriteOffOpen(false)}
                onDone={loadStock}
            />

            <WriteOffRequestModal
                open={writeOffReqOpen}
                onClose={() => setWriteOffReqOpen(false)}
            />

            {/* Оформление чека по фантому — то же окно, что в разделе «Все чеки» */}
            <ProcessSaleModal
                open={processModal.open}
                notification={processModal.notification}
                products={products}
                discounts={discounts}
                categories={categories}
                loading={processModal.loading}
                onClose={() => setProcessModal({ open: false, notification: null, loading: false })}
                onSubmit={handleSubmitProcess}
                canSetDate={false}
                defaultAdminId={processModal.notification?.default_admin_id ?? null}
            />

        </>
    );
}